import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Calendar, MapPin, Sparkles, Search, Wallet, ChevronRight, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { API } from "@/config";

function daysBetween(a, b) {
  if (!a) return 0;
  const s = new Date(a);
  const e = b ? new Date(b) : new Date();
  return Math.max(0, Math.round((e - s) / 86400000));
}

const STATUS_LABELS = {
  planned: { label: "Planifiée", cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "En cours", cls: "bg-[#F0E9F3] text-[#662483]" },
  completed: { label: "Terminée", cls: "bg-green-100 text-green-800" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-800" },
};

export default function HomeScreen() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [offersCount, setOffersCount] = useState(0);
  const [acompteCount, setAcompteCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/me/missions`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/offers?status=active`).catch(() => ({ data: [] })),
      axios.get(`${API}/me/acompte-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([m, o, a]) => {
      if (cancelled) return;
      setMissions(m.data || []);
      setOffersCount((o.data || []).length);
      setAcompteCount((a.data || []).filter((x) => x.status === "pending").length);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const inProgress = missions.filter((m) => m.status === "in_progress");
  const upcoming = missions.filter((m) => m.status === "planned");
  const currentMission = inProgress[0] || null;

  return (
    <div className="p-4 space-y-4 text-sm" data-testid="home-screen">
      {/* Greeting */}
      <div className="bg-gradient-to-br from-[#662483] to-[#0069B4] text-white p-5 -mx-4 -mt-4">
        <p className="text-xs uppercase tracking-wider opacity-80 font-semibold">Bonjour</p>
        <h1 className="text-2xl font-bold mt-1">{user?.first_name || user?.name || "Candidat"}</h1>
        <p className="text-sm opacity-85 mt-1.5">
          Dispo à partir du {user?.disponibilite_debut ? new Date(user.disponibilite_debut).toLocaleDateString("fr-FR") : "—"}
        </p>
      </div>

      {/* Mission en cours */}
      {currentMission ? (
        <Link
          to="/app/missions"
          className="block bg-white border-l-4 border-[#662483] p-4 shadow-sm hover:shadow-md transition-shadow"
          data-testid="home-current-mission"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-[#662483] animate-pulse" />
            <p className="text-xs uppercase tracking-wider text-[#662483] font-bold">Mission en cours</p>
          </div>
          <p className="text-lg font-bold text-[#3F3F3F]">{currentMission.company_name}</p>
          <p className="text-sm text-gray-600 mt-0.5">{currentMission.job_title}</p>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
            <Calendar className="w-3.5 h-3.5" />
            <span>Depuis le {new Date(currentMission.start_date).toLocaleDateString("fr-FR")}</span>
            <span className="mx-1">·</span>
            <Clock className="w-3.5 h-3.5" />
            <span>{daysBetween(currentMission.start_date)} jours</span>
          </div>
        </Link>
      ) : (
        <div className="bg-white border border-dashed border-gray-300 p-6 text-center" data-testid="home-no-mission">
          <Briefcase className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-base font-semibold text-[#3F3F3F]">Pas de mission en cours</p>
          <p className="text-sm text-gray-500 mt-1">Consultez les offres pour postuler.</p>
        </div>
      )}

      {/* Prochaines missions */}
      {upcoming.length > 0 && (
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-xs uppercase tracking-wider text-gray-600 font-bold mb-2">Prochaines missions ({upcoming.length})</p>
          <ul className="divide-y divide-gray-100">
            {upcoming.slice(0, 3).map((m) => (
              <li key={m.mission_id} className="py-2.5 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#662483] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#3F3F3F] truncate">{m.company_name}</p>
                  <p className="text-xs text-gray-500 truncate">{m.job_title} · dès le {new Date(m.start_date).toLocaleDateString("fr-FR")}</p>
                </div>
                <Badge className={`rounded-none text-xs ${STATUS_LABELS[m.status]?.cls || ""}`}>{STATUS_LABELS[m.status]?.label}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/app/offres" className="bg-white border border-gray-200 p-4 hover:border-[#662483] transition-colors group" data-testid="home-quick-offers">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 bg-[#F0E9F3] flex items-center justify-center group-hover:bg-[#E6D9EE] transition-colors shrink-0">
              <Search className="w-5 h-5 text-[#662483]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-600 font-bold">Offres</p>
              <p className="text-lg font-bold text-[#3F3F3F]">{loading ? "…" : offersCount}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
          </div>
        </Link>
        <Link to="/app/acompte" className="bg-white border border-gray-200 p-4 hover:border-[#662483] transition-colors group" data-testid="home-quick-acompte">
          <div className="flex items-center gap-2.5">
            <div className="w-11 h-11 bg-[#E6F0F7] flex items-center justify-center group-hover:bg-[#D4E5F0] transition-colors shrink-0">
              <Wallet className="w-5 h-5 text-[#0069B4]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-600 font-bold">Acomptes</p>
              <p className="text-lg font-bold text-[#3F3F3F]">{loading ? "…" : `${acompteCount} en attente`}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
          </div>
        </Link>
      </div>

      {/* Preferences summary */}
      {(user?.job_preferences || []).length > 0 && (
        <div className="bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#662483]" />
            <p className="text-xs uppercase tracking-wider text-gray-600 font-bold">Mes préférences</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(user.job_preferences || []).map((p) => (
              <Badge key={p} className="rounded-none text-xs bg-[#F0E9F3] text-[#662483] px-2 py-1">{p}</Badge>
            ))}
            {user.mobility_km && (
              <Badge className="rounded-none text-xs bg-gray-100 text-gray-700 px-2 py-1"><MapPin className="w-3 h-3 inline mr-0.5" />{user.mobility_km} km</Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
